import React, { useState, useEffect } from "react";
import Select from "react-select";
import type {
  ControlProps,
  OptionProps,
  MenuProps,
  MultiValueProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
  NoticeProps,
  MultiValue,
} from "react-select";
import { makeStyles } from "@mui/styles";
import { emphasize } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import ChipCobertura from "~shared/chipCobertura/ChipCobertura";
import { useSelector } from "react-redux";
import {
  EsquemaRow,
  IdCobertura,
  TipoCoberturaValue,
  TipoCoberturaOption,
} from "../types";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  input: {
    display: "flex",
    padding: 0,
    height: "auto",
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
  },
  chip: {
    margin: theme.spacing(0.5, 0.25),
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.mode === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: "absolute",
    left: 2,
    bottom: 6,
    fontSize: 16,
  },
  paper: {
    position: "absolute",
    zIndex: 2,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(2),
  },
}));

interface CoberturaOption {
  value: string;
  label: string;
  tipoCobertura: TipoCoberturaValue;
}

const useReduxSelector = () =>
  useSelector((state: any) => ({
    tiposCobertura: state.tipoCoberturaReducer
      .tiposCobertura as TipoCoberturaOption[],
  }));

interface CoberturasSelectorProps {
  value: EsquemaRow;
  idsCoberturas?: IdCobertura[] | null;
  onChangeCoberturas: (coberturas: IdCobertura[] | null) => void;
}

const CoberturasSelector: React.FC<CoberturasSelectorProps> = ({
  value,
  idsCoberturas,
  onChangeCoberturas,
}) => {
  const classes = useStyles();

  const [coberturas, setCoberturas] =
    useState<MultiValue<CoberturaOption> | null>(null);

  useEffect(() => {
    setCoberturas(
      idsCoberturas
        ? idsCoberturas.map((id) => ({
            value: id.idCobertura,
            label: id.idCobertura,
            tipoCobertura: id.tipoCobertura,
          }))
        : null,
    );
  }, [idsCoberturas]);

  const sugerencias: CoberturaOption[] = value.sugerencias.map(
    (sugerencia) => ({
      value: sugerencia.idCobertura,
      label: sugerencia.idCobertura,
      tipoCobertura: sugerencia.tipoCobertura,
    }),
  );

  const handleChangeCoberturas = (
    newValue: MultiValue<CoberturaOption> | null,
  ) => {
    onChangeCoberturas(
      newValue
        ? newValue.map((id) => ({
            idCobertura: id.value,
            tipoCobertura: id.tipoCobertura,
          }))
        : null,
    );
    setCoberturas(newValue);
  };

  return (
    <div className={classes.root}>
      {value && (
        <Select<CoberturaOption, true>
          {...({
            classes,
            TextFieldProps: {
              InputLabelProps: {
                htmlFor: "react-select-multiple",
                shrink: true,
              },
            },
          } as any)}
          inputId="react-select-multiple"
          placeholder="Coberturas"
          options={sugerencias}
          components={components}
          value={coberturas}
          onChange={handleChangeCoberturas}
          isMulti
          isClearable={false}
        />
      )}
    </div>
  );
};

export default CoberturasSelector;

const NoOptionsMessage: React.FC<NoticeProps<CoberturaOption, true>> = () => {
  const classes = useStyles();
  return (
    <Typography color="textSecondary" className={classes.noOptionsMessage}>
      {"No hay más coberturas posibles"}
    </Typography>
  );
};

const inputComponent: any = ({
  inputRef,
  ...props
}: {
  inputRef?: React.Ref<HTMLDivElement>;
} & React.HTMLAttributes<HTMLDivElement>) => {
  return <div ref={inputRef} {...props} />;
};

const Control: React.FC<ControlProps<CoberturaOption, true>> = (props) => {
  const { children, innerProps, innerRef, selectProps } = props;
  const { classes, TextFieldProps } = selectProps as any;

  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
};

const Option: React.FC<OptionProps<CoberturaOption, true>> = (props) => {
  const { tiposCobertura } = useReduxSelector();
  const tipoCobertura = tiposCobertura.find(
    (tipo) => tipo.id === props.data.tipoCobertura,
  );
  const { ref: _ref, ...safeInnerProps } = props.innerProps;

  return (
    <MenuItem
      ref={props.innerRef as React.Ref<HTMLDivElement>}
      selected={props.isFocused}
      component="div"
      style={{ fontWeight: props.isSelected ? 500 : 400 }}
      {...safeInnerProps}
    >
      {props.children} {tipoCobertura ? `(${tipoCobertura.nombre})` : ""}
    </MenuItem>
  );
};

const Placeholder: React.FC<PlaceholderProps<CoberturaOption, true>> = (
  props,
) => {
  const { innerProps = {}, children } = props;
  const classes = useStyles();
  const { ref: _ref, ...safeInnerProps } = innerProps;
  return (
    <Typography
      color="textSecondary"
      className={classes.placeholder}
      {...safeInnerProps}
    >
      {children}
    </Typography>
  );
};

const SingleValue: React.FC<SingleValueProps<CoberturaOption, true>> = (
  props,
) => {
  const { classes } = props.selectProps as any;
  const { ref: _ref, ...safeInnerProps } = props.innerProps;
  return (
    <Typography className={classes.singleValue} {...safeInnerProps}>
      {props.children}
    </Typography>
  );
};

const ValueContainer: React.FC<ValueContainerProps<CoberturaOption, true>> = (
  props,
) => {
  const classes = useStyles();
  return <div className={classes.valueContainer}>{props.children}</div>;
};

const MultiValue: React.FC<MultiValueProps<CoberturaOption, true>> = (
  props,
) => {
  return (
    <div style={{ padding: "5px 5px 0px 0px" }}>
      <ChipCobertura
        label={props.children}
        onClick={undefined}
        onDelete={props.removeProps.onClick}
        tipoCobertura={props.data.tipoCobertura}
        clickable={false}
      />
    </div>
  );
};

const Menu: React.FC<MenuProps<CoberturaOption, true>> = (props) => {
  const classes = useStyles();
  const { ref: _ref, ...safeInnerProps } = props.innerProps;
  return (
    <Paper square className={classes.paper} {...safeInnerProps}>
      {props.children}
    </Paper>
  );
};

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};
